import { useMutation, useQuery } from "@apollo/client/react";
import { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { useEngagementTracking } from "../../hooks/useEngagementTracking";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import { captureMessage } from "@sentry/nextjs";
import { logger } from "@portal/app/_common/lib/logger";
import { getUrls } from "@portal/app/_common/lib/urls";
import { gql } from "graphql-tag";
import { useAuthenticatedContext } from "@portal/app/_components/context/AuthenticatedContext";
import {
  GetViewerForHostedResultDocument,
  UpdateCustomerHostedResultDocument,
  UpdateCustomerDetailsHostedResultDocument,
  CreateCustomerHostedResultDocument,
} from "./generated";
import { ContactStep, PhoneStep, AddressStep, ConfirmStep, HostedDynamicStep } from "./hostedSteps";
import type { CustomerDataBuilder } from "./hostedSteps";
import type {
  HostedFlowConfig,
  HostedFlowStep,
  PayloadFormQuestion,
  PayloadProgramHighlight,
  QuestionDescriptionConfig,
} from "@portal/app/_server/services/formFunnel/payloadTypes";
import { DEFAULT_HOSTED_FLOW_STEPS } from "@portal/app/_server/services/formFunnel/payloadTypes";
import type {
  QuestionConfig,
  FieldType,
} from "@portal/app/_components/formFunnel/FormFieldRenderer";
import { transformValue, buildMutationVariables } from "./hostedSteps/utils";
import {
  getCustomerFieldForStep,
  shouldRenderHostedDynamicStep,
} from "./hostedSteps/hostedStepRouting";
import { buildPreCollectedSteps } from "./hostedSteps/buildPreCollectedSteps";
import {
  formatCustomerFieldValue,
  populateCustomerDataRef,
} from "./hostedSteps/preCollectedFieldProcessor";
import { buildCmsQuestionMap } from "./hostedSteps/buildCmsQuestionMap";
import type { ProgramEnvironment } from "@portal/app/_common/types/program";
import { applyGradYearBounds } from "./hostedSteps/applyGradYearBounds";
import { useFormFunnelTracking } from "../hooks/useFormFunnelTracking";
import type { DobFieldVisibility } from "@portal/app/_common/types/program";
import type { VocationIndustry } from "@portal/app/_common/types/vocationIndustry";
import {
  getHostedProgressPosition,
  type FunnelProgressUpdate,
  type PhoneProgressStage,
  type ProgressDirection,
} from "../utils/funnelProgress";

void gql`
  query getViewerForHostedResult {
    viewerCustomer {
      id
      detailsMap {
        address {
          street
          street2
          city
          state
          zip
        }
        isUsCitizen
        graduatedHighSchoolIn
        hasRNLicense
        hasMilitaryAffiliation
        dob
        educationLevel
        acceptedTermsVersion
        email
        phone
        careerGoal
        careerChangeConcern
        careerTimeline
        careerAspectsRanking
        careerAspectsDesired
        jobEnjoyment
        jobDislike
        coachStyle
        degreeLevelInterest
        furtherEducationReason
        industryInterest
        mainFocus
        subindustryInterest
        timeframePreference
        enrollLikelihood
        programEnvironment
      }
      user {
        id
        nameFirst
        nameLast
        email
        phone
        phoneVerified
      }
    }
  }

  mutation updateCustomerHostedResult($data: CustomerUpdateInput!) {
    updateViewerCustomer(data: $data) {
      id
      user {
        id
        nameFirst
        nameLast
        email
        phone
        phoneVerified
      }
    }
  }

  mutation updateCustomerDetailsHostedResult($data: CustomerDetailsUpdateInput!) {
    updateViewerCustomerDetails(data: $data) {
      id
    }
  }

  mutation createCustomerHostedResult($data: CustomerAndEnrollmentItemCreateInput!) {
    createOneCustomer(data: $data) {
      customer {
        id
        user {
          id
          email
          tokens {
            accessToken
            refreshToken
          }
        }
      }
    }
  }

  mutation requestPhoneCodeHosted($phone: String!, $isMobile: Boolean!) {
    requestCodeViaPhone(phone: $phone, isMobile: $isMobile)
  }

  mutation verifyPhoneCodeHosted($phone: String!, $code: String!) {
    verifyPhoneCode(phone: $phone, code: $code) {
      id
      phoneVerified
    }
  }
`;

// --- CMS mapping for dynamic hosted steps ---

/** Convert a Payload step-description group into a QuestionConfig['description']. */
function toStepDescription(config?: QuestionDescriptionConfig) {
  if (!config?.content) return undefined;
  return {
    content: config.content,
    placement: config.placement,
  };
}

/** Convert a PayloadFormQuestion to a QuestionConfig for FormFieldRenderer */
function toQuestionConfig(q: PayloadFormQuestion): QuestionConfig {
  return {
    id: q.fieldKey,
    label: q.label,
    type: q.type as FieldType,
    options: q.options || [],
    required: q.validation?.required ?? false,
    autocomplete: q.autocomplete,
    inputMode: q.inputMode,
    description: q.description?.content
      ? {
          content: q.description.content,
          placement: q.description.placement,
        }
      : undefined,
  };
}

// --- Types ---

export interface HostedResultProps {
  program: {
    id: string;
    name: string;
    environment?: ProgramEnvironment;
    programHighlights?: string[];
    provider: {
      id: string;
      shortName: string;
      fullName: string;
      advertiserName: string;
      isTrustedFormEnabled: boolean;
      successPageMessage?: string | null;
      logoImageFile?: { url: string | null } | null;
    };
    customCtaCopy?: string | null;
    customDisclaimer?: string | null;
    allowedZipCodes?: string[];
    dobFieldVisibility: DobFieldVisibility;
    hsGradYearMin?: number | null;
    hsGradYearMax?: number | null;
    vocation?: { id: string; industry: VocationIndustry };
    hasFinancialAidOption?: boolean;
  };
  hostedFlowConfig?: HostedFlowConfig;
  /** Populated funnel questions from CMS (used for dynamic hosted steps) */
  funnelQuestions?: PayloadFormQuestion[];
  /** Static program highlights from CMS config. Overrides program.programHighlights when provided. */
  staticProgramHighlights?: PayloadProgramHighlight[];
  onComplete?: (redirectUrl?: string, firstName?: string) => void;
  /** When true, disables lead submission and shows preview indicators */
  isPreview?: boolean;
  /** Form funnel ID, threaded to ConfirmStep for lead submission */
  funnelId?: string;
  /** Per-tab form funnel attempt ID, threaded to ConfirmStep for enrollment linking */
  formFunnelSessionId?: string;
  /** Collected form answers, threaded to ConfirmStep for lead submission */
  answers?: Record<string, unknown>;
  /** Scoped fallback destination if the program becomes unavailable during submission. */
  searchUrl?: string;
  /** Callback to report current hosted step progress to parent */
  onProgressChange?: (progress: FunnelProgressUpdate) => void;
  /** Exits the hosted flow when the user backs out of its first effective step. */
  onBackFromFirstStep?: () => void;
  /**
   * Pre-ping result payload, threaded to ConfirmStep so the submit mutation
   * can persist persistedFields onto enrollment_item.metadata.prePingResult.
   */
  prePingResult?: unknown;
  /** Displayed result-card rank selected by the student. */
  selectedResultRank?: number;
  /** Forwarded to ConfirmStep — see ConfirmStepProps.onSubmittingChange. */
  onSubmittingChange?: (isSubmitting: boolean) => void;
}

// --- Component ---

export const HostedResult = ({
  program,
  hostedFlowConfig,
  funnelQuestions,
  staticProgramHighlights,
  onComplete,
  isPreview = false,
  funnelId,
  formFunnelSessionId,
  answers,
  searchUrl,
  onProgressChange,
  onBackFromFirstStep,
  prePingResult,
  selectedResultRank,
  onSubmittingChange,
}: HostedResultProps) => {
  // Determine which steps to show based on config, defaulting to all 4 in standard order
  const activeSteps = useMemo<HostedFlowStep[]>(() => {
    const configuredSteps = hostedFlowConfig?.steps;
    if (configuredSteps && configuredSteps.length > 0) {
      return configuredSteps;
    }
    return DEFAULT_HOSTED_FLOW_STEPS;
  }, [hostedFlowConfig?.steps]);

  const [navigation, setNavigation] = useState({
    stepIndex: 0,
    phoneStage: "phone" as PhoneProgressStage,
    generation: 0,
    direction: "forward" as ProgressDirection,
  });
  const { stepIndex, generation } = navigation;
  const navigationRef = useRef(navigation);
  const completedGenerationRef = useRef<number | null>(null);
  navigationRef.current = navigation;
  const { trackHostedFlowStep } = useFormFunnelTracking();
  const [zipRestricted, setZipRestricted] = useState(false);
  const { loggedIn } = useAuthenticatedContext();

  // Track collected customer data across steps
  const customerDataRef = useRef<CustomerDataBuilder>({
    nameFirst: "",
    nameLast: "",
    email: "",
    phone: "",
  });

  const { data: viewerData, refetch: refetchViewer } = useQuery(GetViewerForHostedResultDocument, {
    skip: !loggedIn,
  });
  const viewerCustomer = viewerData?.viewerCustomer;
  const user = viewerCustomer?.user || null;

  // Snapshot the viewer's identity at the moment HostedResult first knows
  // who it's rendering for. The `effectiveSteps` filter below uses this
  // snapshot (NOT live `user`) to decide which steps to skip.
  //
  // Why: ContactStep submits + refetches the viewer mid-flow. If the skip
  // filter ran on the live `user`, refetching would shrink `effectiveSteps`
  // while `stepIndex` was a numeric counter — the user would silently
  // advance past PhoneStep into AddressStep. Capturing the snapshot once
  // keeps the step list stable for the lifetime of the component, so
  // `stepIndex` always points where the user expects. ENG-6248.
  const initialUserSnapshotRef = useRef<{
    nameFirst: string | null;
    nameLast: string | null;
    email: string | null;
    phoneVerified: boolean | null;
  } | null>(null);
  if (initialUserSnapshotRef.current === null) {
    if (!loggedIn) {
      // Anon visitor — no fields to skip; an account created mid-flow does
      // NOT retroactively cause those steps to be filtered.
      initialUserSnapshotRef.current = {
        nameFirst: null,
        nameLast: null,
        email: null,
        phoneVerified: false,
      };
    } else if (viewerData !== undefined) {
      // Logged in AND viewer query resolved. Capture once; subsequent
      // refetches don't change the skip decision.
      initialUserSnapshotRef.current = user
        ? {
            nameFirst: user.nameFirst,
            nameLast: user.nameLast,
            email: user.email,
            phoneVerified: user.phoneVerified,
          }
        : {
            nameFirst: null,
            nameLast: null,
            email: null,
            phoneVerified: false,
          };
    }
    // else: logged in but viewer query still loading — leave null and let
    // the render below short-circuit to a spinner.
  }
  const initialUser = initialUserSnapshotRef.current;

  const [updateCustomer] = useMutation(UpdateCustomerHostedResultDocument);
  const [updateCustomerDetails] = useMutation(UpdateCustomerDetailsHostedResultDocument);
  const [createCustomer] = useMutation(CreateCustomerHostedResultDocument);

  // Build a lookup of CMS questions by customerField for dynamic hosted steps.
  // First entry per customerField wins — prevents orphaned customerMappedQuestions
  // from overwriting funnel-linked questions and breaking pre-collection deduplication.
  const cmsQuestionMap = useMemo(() => buildCmsQuestionMap(funnelQuestions), [funnelQuestions]);

  // Constrain the GRAD_YEAR step to the matched program's allowed range — the
  // filtered options and the step's Yup bounds come from the same computation.
  // Memoized so the misconfigured-bounds warning fires once, not per render.
  const gradYearCmsQuestion = cmsQuestionMap.get("graduatedHighSchoolIn");
  const gradYear = useMemo(
    () =>
      applyGradYearBounds(
        gradYearCmsQuestion?.options || undefined,
        program.hsGradYearMin,
        program.hsGradYearMax
      ),
    [gradYearCmsQuestion, program.hsGradYearMin, program.hsGradYearMax]
  );

  // Identify answers already collected in the form questionnaire phase that map to
  // customer fields. Hosted steps in this map are skipped in the UI; all entries
  // (including non-hosted fields like DOB) are saved to Customer after login.
  const preCollectedSteps = useMemo(
    () => buildPreCollectedSteps({ activeSteps, answers, cmsQuestionMap }),
    [activeSteps, answers, cmsQuestionMap]
  );

  // Filter out pre-collected steps from the active step list. Also skip
  // contact/phone when the viewer ALREADY HAD those fields when this
  // component first rendered (returning user, or freshly verified via
  // PreMatchHostedFlow). Uses the initial-mount snapshot so the step list
  // stays stable mid-flow — see initialUserSnapshotRef above for rationale.
  const effectiveSteps = useMemo(
    () =>
      activeSteps.filter((step) => {
        if (preCollectedSteps.has(step)) return false;
        if (
          step === "contact" &&
          initialUser?.nameFirst &&
          initialUser?.nameLast &&
          initialUser?.email
        ) {
          return false;
        }
        if (step === "phone" && initialUser?.phoneVerified) {
          return false;
        }
        return true;
      }),
    [activeSteps, preCollectedSteps, initialUser]
  );

  // Identity skips must resolve before the parent sees a hosted denominator.
  // A verified phone retained after a mid-flow refetch presents the code unit.
  const phoneStage = user?.phoneVerified ? "code" : navigation.phoneStage;
  const { stageIndex: progressStageIndex, totalStages } = getHostedProgressPosition(
    effectiveSteps,
    stepIndex,
    phoneStage
  );
  useEffect(() => {
    if (initialUser === null) return;
    onProgressChange?.({
      phase: "hosted",
      stageIndex: progressStageIndex,
      totalStages,
      direction: navigation.direction,
    });
  }, [initialUser, progressStageIndex, totalStages, navigation.direction, onProgressChange]);

  const handlePhoneProgressStageChange = useCallback(
    (stage: PhoneProgressStage) => {
      setNavigation((current) => {
        if (
          current.generation !== generation ||
          current.stepIndex !== stepIndex ||
          current.phoneStage === stage
        ) {
          return current;
        }
        return { ...current, phoneStage: stage, direction: "forward" };
      });
    },
    [generation, stepIndex]
  );

  // Populate customerDataRef with pre-collected values immediately (before login).
  // This ensures ConfirmStep always has educationLevel/dob regardless of whether
  // the DB save succeeds. Runs once when preCollectedSteps first populates.
  const preCollectedRefsPopulated = useRef(false);
  useEffect(() => {
    if (preCollectedRefsPopulated.current || preCollectedSteps.size === 0) return;
    for (const [, { value, customerField }] of preCollectedSteps) {
      const stringValue = formatCustomerFieldValue(value, customerField);
      populateCustomerDataRef(customerDataRef, customerField, stringValue);
    }
    preCollectedRefsPopulated.current = true;
  }, [preCollectedSteps]);

  // Backfill customerDataRef from the viewer when the contact/phone steps were
  // skipped (because the User record already has those fields — including
  // anyone who completed pre-match contact collection in this same session,
  // ENG-6248). Without this, ConfirmStep's `isCompleteCustomerData` check
  // would fail because the ref still holds the empty defaults.
  useEffect(() => {
    if (!user) return;
    if (user.nameFirst && !customerDataRef.current.nameFirst) {
      customerDataRef.current.nameFirst = user.nameFirst;
    }
    if (user.nameLast && !customerDataRef.current.nameLast) {
      customerDataRef.current.nameLast = user.nameLast;
    }
    if (user.email && !customerDataRef.current.email) {
      customerDataRef.current.email = user.email;
    }
    if (user.phoneVerified && user.phone && !customerDataRef.current.phone) {
      customerDataRef.current.phone = user.phone;
    }
  }, [user]);

  // Save pre-collected eligibility values to Customer DB record after login.
  // Fires when loggedIn becomes true (after ContactStep creates/logs in the user).
  const [preCollectedSaved, setPreCollectedSaved] = useState(false);

  useEffect(() => {
    if (!loggedIn || preCollectedSaved || preCollectedSteps.size === 0) return;
    let cancelled = false;
    const save = async () => {
      try {
        for (const [, { value, customerField, valueTransform }] of preCollectedSteps) {
          try {
            const stringValue = formatCustomerFieldValue(value, customerField);
            const transformed = transformValue(stringValue, valueTransform);
            const mutationVars = buildMutationVariables(customerField, transformed);
            await updateCustomerDetails({ variables: { data: mutationVars } });
          } catch (fieldError) {
            logger.error(fieldError, `Failed to save pre-collected field: ${customerField}`);
            captureMessage(
              `Pre-collected save failed for field ${customerField}: ${fieldError instanceof Error ? fieldError.message : String(fieldError)}`,
              "error"
            );
          }
        }
        await refetchViewer();
      } finally {
        if (!cancelled) setPreCollectedSaved(true);
      }
    };
    void save();
    return () => {
      cancelled = true;
    };
  }, [loggedIn, preCollectedSaved, preCollectedSteps, updateCustomerDetails, refetchViewer]);

  const navigate = (direction: ProgressDirection) => {
    const current = navigationRef.current;
    // Async work from a phone screen left with Back cannot move its replacement.
    if (current.generation !== generation || current.stepIndex !== stepIndex) return;
    const nextIndex =
      direction === "back"
        ? Math.max(0, current.stepIndex - 1)
        : Math.min(current.stepIndex + 1, effectiveSteps.length - 1);
    if (nextIndex === current.stepIndex) return;
    const next: typeof navigation = {
      stepIndex: nextIndex,
      phoneStage: "phone",
      generation: current.generation + 1,
      direction,
    };
    navigationRef.current = next;
    setNavigation(next);
  };
  const handleNext = () => {
    const current = navigationRef.current;
    if (current.generation !== generation || current.stepIndex !== stepIndex) return;
    if (completedGenerationRef.current === generation) return;
    completedGenerationRef.current = generation;
    if (!isPreview) trackHostedFlowStep(effectiveSteps[stepIndex]);
    navigate("forward");
  };
  const handleBack = () => navigate("back");

  const handleAddressNext = () => {
    const addressZip = customerDataRef.current.address?.zip?.trim().slice(0, 5);
    const allowed = program.allowedZipCodes;
    if (allowed && allowed.length > 0 && addressZip && !allowed.includes(addressZip)) {
      setZipRestricted(true);
      return;
    }
    handleNext();
  };

  // Get the current step type based on the effective steps array
  const currentStepType = effectiveSteps[stepIndex];
  useEngagementTracking(
    !isPreview &&
      initialUser !== null &&
      !zipRestricted &&
      currentStepType &&
      !(
        currentStepType === "confirm" &&
        loggedIn &&
        preCollectedSteps.size > 0 &&
        !preCollectedSaved
      )
      ? {
          scope: "step",
          phase: "hosted",
          stepId: currentStepType,
          stepIndex,
          formFunnelId: funnelId,
          formFunnelSessionId,
          programId: program.id,
          providerId: program.provider.id,
        }
      : null
  );
  const isFirstStep = stepIndex === 0;
  const onBack = isFirstStep ? onBackFromFirstStep : handleBack;

  // Render the appropriate step component based on current step type
  const renderCurrentStep = () => {
    // Check if this step can be handled by HostedDynamicStep via CMS config
    const customerField = getCustomerFieldForStep(currentStepType);
    const cmsQuestion = cmsQuestionMap.get(customerField);
    if (shouldRenderHostedDynamicStep(currentStepType, cmsQuestion)) {
      const questionConfig = toQuestionConfig(cmsQuestion);
      const isGradYearStep =
        cmsQuestion.customerFieldMapping.customerField === "graduatedHighSchoolIn";
      if (isGradYearStep) {
        questionConfig.options = gradYear.options ?? [];
      }
      return (
        <HostedDynamicStep
          viewerCustomer={viewerCustomer}
          customerDataRef={customerDataRef}
          updateCustomerDetails={updateCustomerDetails}
          refetchViewer={refetchViewer}
          onNext={handleNext}
          onBack={onBack}
          questionConfig={questionConfig}
          customerFieldMapping={{
            customerField: cmsQuestion.customerFieldMapping.customerField,
            valueTransform: cmsQuestion.customerFieldMapping.valueTransform || "none",
          }}
          validationConfig={cmsQuestion.validation}
          gradYearMin={isGradYearStep ? gradYear.bounds?.min : undefined}
          gradYearMax={isGradYearStep ? gradYear.bounds?.max : undefined}
        />
      );
    }

    // Complex steps remain hardcoded
    switch (currentStepType) {
      case "contact":
        return (
          <ContactStep
            user={user}
            loggedIn={loggedIn}
            viewerData={viewerData}
            customerDataRef={customerDataRef}
            updateCustomer={updateCustomer}
            createCustomer={createCustomer}
            refetchViewer={refetchViewer}
            onNext={handleNext}
            onBack={onBack}
            description={toStepDescription(hostedFlowConfig?.stepDescriptions?.contact)}
          />
        );
      case "phone":
        return (
          <PhoneStep
            key={generation}
            user={user}
            customerDataRef={customerDataRef}
            refetchViewer={refetchViewer}
            onNext={handleNext}
            onBack={onBack}
            onProgressStageChange={handlePhoneProgressStageChange}
            description={toStepDescription(hostedFlowConfig?.stepDescriptions?.phone)}
          />
        );
      case "address":
        return (
          <AddressStep
            viewerCustomer={viewerCustomer}
            customerDataRef={customerDataRef}
            updateCustomerDetails={updateCustomerDetails}
            refetchViewer={refetchViewer}
            onNext={handleAddressNext}
            onBack={onBack}
            description={toStepDescription(hostedFlowConfig?.stepDescriptions?.address)}
          />
        );
      case "dob":
        // DOB step driven by program.dobFieldVisibility — render via
        // HostedDynamicStep with a synthetic config when no CMS question exists.
        // When OPTIONAL, HostedDynamicStep renders a Skip button alongside
        // the Continue button so the user can advance without a value.
        return (
          <HostedDynamicStep
            viewerCustomer={viewerCustomer}
            customerDataRef={customerDataRef}
            updateCustomerDetails={updateCustomerDetails}
            refetchViewer={refetchViewer}
            onNext={handleNext}
            onBack={onBack}
            questionConfig={{
              id: "dob",
              label: "Date of Birth",
              type: "date" as FieldType,
              options: [],
              required: program.dobFieldVisibility === "REQUIRED",
            }}
            customerFieldMapping={{ customerField: "dob", valueTransform: "none" }}
          />
        );
      case "confirm":
        // Wait for pre-collected values to be saved to the DB before allowing submission.
        // Ref population happens immediately (separate effect), but the DB save requires
        // an authenticated session. Block until the DB save completes or fails.
        if (loggedIn && preCollectedSteps.size > 0 && !preCollectedSaved) {
          return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          );
        }
        return (
          <ConfirmStep
            program={program}
            user={user}
            customerDataRef={customerDataRef}
            funnelId={funnelId || ""}
            formFunnelSessionId={formFunnelSessionId}
            answers={answers || {}}
            searchUrl={searchUrl}
            onComplete={onComplete}
            onBack={onBack}
            isPreview={isPreview}
            // Priority: program.customCtaCopy (Staff Portal — owned by the school for
            // compliance) wins over hostedFlowConfig.ctaButtonText (Payload — owned by
            // marketing/ops). Funnel-level only fills in when the school left their
            // CTA blank. ENG-6234.
            ctaButtonText={program.customCtaCopy || hostedFlowConfig?.ctaButtonText || undefined}
            postLeadRedirectMode={hostedFlowConfig?.postLeadRedirectMode}
            prePingResult={prePingResult}
            selectedResultRank={selectedResultRank}
            onSubmittingChange={onSubmittingChange}
            onValidatedSubmit={(scenario) => {
              if (!isPreview) {
                trackHostedFlowStep(scenario === "HOSTED" ? "confirm" : "clickout_handoff");
              }
            }}
            programSummary={programSummary}
          />
        );
      default:
        logger.warn(
          `HostedResult: unhandled step "${currentStepType}" (customerField: "${customerField}")`
        );
        return <Typography color="error">Configuration error. Please try again later.</Typography>;
    }
  };

  // --- Highlights rendering ---

  const renderHighlightsSection = () => {
    if (staticProgramHighlights && staticProgramHighlights.length > 0) {
      return (
        <Box
          component="ul"
          data-testid="hosted-program-highlights"
          sx={{
            m: 0,
            pl: 2.25,
            color: "dream.grayMediumDark",
            fontSize: "0.9rem",
            lineHeight: 1.5,
            wordWrap: "break-word",
          }}
        >
          {staticProgramHighlights.map((highlight, index) => (
            <Typography
              key={`${highlight.icon ?? ""}-${highlight.text}-${index}`}
              component="li"
              variant="body2"
              color="text.secondary"
              sx={{ mb: 0.5 }}
            >
              {highlight.icon && (
                <Box component="span" sx={{ mr: 0.75 }} aria-hidden>
                  {highlight.icon}
                </Box>
              )}
              {highlight.heading && (
                <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
                  {highlight.heading}:{" "}
                </Box>
              )}
              {highlight.text}
            </Typography>
          ))}
        </Box>
      );
    }

    if (program.programHighlights && program.programHighlights.length > 0) {
      return (
        <Box
          component="ul"
          data-testid="hosted-program-highlights"
          sx={{
            m: 0,
            pl: 2.25,
            color: "dream.grayMediumDark",
            fontSize: "0.9rem",
            lineHeight: 1.5,
            wordWrap: "break-word",
          }}
        >
          {program.programHighlights.map((highlight) => (
            <Typography
              key={highlight}
              component="li"
              variant="body2"
              color="text.secondary"
              sx={{ mb: 0.5 }}
            >
              {highlight}
            </Typography>
          ))}
        </Box>
      );
    }

    return null;
  };

  const highlightsContent = renderHighlightsSection();
  const providerInitial = Array.from(program.provider.fullName.trim())[0]?.toUpperCase() || "?";
  const programSummary = (
    <>
      {hostedFlowConfig?.programSuggestionHeading && (
        <Typography
          component="p"
          sx={{ mb: 2, fontSize: "1.1rem", fontWeight: 700, lineHeight: 1.3 }}
          data-testid="program-suggestion-heading"
        >
          {hostedFlowConfig.programSuggestionHeading}
        </Typography>
      )}
      <Box
        data-testid="hosted-program-card"
        sx={{
          mb: 2.75,
          p: 2,
          border: "1px solid",
          borderColor: "dream.borderColor",
          borderRadius: "20px",
          backgroundColor: "common.white",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
          {program.provider.logoImageFile?.url ? (
            <Box
              component="img"
              src={program.provider.logoImageFile.url}
              alt={`${program.provider.fullName} logo`}
              data-testid="hosted-provider-logo"
              sx={{
                width: 40,
                height: 40,
                flexShrink: 0,
                borderRadius: "50%",
                objectFit: "contain",
                backgroundColor: "dream.grayLighter",
              }}
            />
          ) : (
            <Box
              data-testid="hosted-provider-initial"
              aria-label={`${program.provider.fullName} logo fallback`}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 40,
                height: 40,
                flexShrink: 0,
                borderRadius: "50%",
                backgroundColor: "#e8f2ff",
                color: "primary.main",
                fontSize: "1.05rem",
                fontWeight: 800,
              }}
            >
              {providerInitial}
            </Box>
          )}
          <Typography
            sx={{
              color: "dream.grayMediumDark",
              fontSize: "0.74rem",
              fontWeight: 700,
              letterSpacing: "0.08em",
              lineHeight: 1.3,
              textTransform: "uppercase",
            }}
          >
            {program.provider.fullName}
          </Typography>
        </Box>
        <Typography
          sx={{
            color: "text.primary",
            fontSize: "1.05rem",
            fontWeight: 700,
            lineHeight: 1.3,
            mb: highlightsContent ? 1.25 : 0,
          }}
        >
          {program.name}
        </Typography>
        {highlightsContent}
      </Box>
    </>
  );

  // --- Main Render ---

  return (
    <Box
      data-testid="hosted-result"
      sx={{
        maxWidth: 430,
        mx: "auto",
        p: "24px 20px 32px",
        border: "1px solid",
        borderColor: "dream.borderColor",
        borderRadius: "20px",
        backgroundColor: "common.white",
      }}
    >
      {zipRestricted ? (
        <Box data-testid="zip-code-restricted-warning">
          <Alert severity="warning" sx={{ mb: 2 }}>
            Unfortunately, this program is currently not available in your ZIP code area.
          </Alert>
          <Button variant="contained" fullWidth href={getUrls().Explore()}>
            Browse other classes
          </Button>
        </Box>
      ) : initialUser === null ? (
        // Logged in but viewer query still loading. Render a spinner instead
        // of the first step — otherwise we'd flash ContactStep before the
        // snapshot-based filter resolved and possibly hid it. ENG-6248.
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        renderCurrentStep()
      )}
    </Box>
  );
};
